/* Headline price + material cards follow the claimed coupon.
 *
 * The buy box already told one story everywhere except the two places the eye
 * lands first: the big price above the picker, and the material cards. The set
 * rows, the upsell rows and the button pill all drop to the payable the moment
 * a code is claimed; the headline kept quoting the pre-coupon figure, so the
 * page argued with itself.
 *
 * Why a converger and not a write. hc-product-variant-picker-v2 `_applyVariant`
 * calls `_updatePriceBlock` immediately and again at +100ms, +500ms and
 * +1500ms, each time writing `variant.price` — the UNDISCOUNTED figure. Anything
 * that merely writes after a variant change is overwritten up to a second and a
 * half later. So this states the desired content and converges the DOM to it
 * through a MutationObserver, exactly the way afroyla-atc-price-pill.js settles
 * the same fight over the button.
 *
 * Termination: paint() writes only when the DOM differs from the desired text.
 * Our own write triggers one more observer pass, which finds it already correct
 * and writes nothing, so the loop closes after a single correction.
 *
 * Where the numbers come from. The coupon strip is the single source: it
 * carries data-price-cents for the current variant (its own variant listener
 * keeps that current) and data-savings-cents floored the way Shopify floors it.
 * Deriving the headline from the same attributes is what guarantees it agrees
 * with the pill and the strip to the cent — 13195 − floor(13195 × 50 / 100)
 * = 6598, not 6597.
 *
 * Restoring on unclaim. `_updatePriceBlock` owns the truth while no code is
 * claimed, so this never touches the DOM in that state beyond undoing its own
 * marks: the payable is replaced by the last foreign write seen for this
 * element, which is by definition the real variant price.
 *
 * Not touched, deliberately: product grids and recommendation apps (other
 * products, other coverage), and the shipping rates in the accordion. Only
 * price nodes inside a [data-price-wrapper] that shares a section with a
 * coupon strip are in scope. */
(function () {
  'use strict';

  var WRAP = '[data-price-wrapper]';
  var PRICE = '[data-product-price]';
  var STRIP = '[data-coupon-strip]';
  var CARD_PRICE = '.hc-vpv2__card-price[data-card-cents]';

  var painting = false;

  function formatMoney(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, window.Shopify.money_format);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /* A quick-add modal puts a second buy box on the page, so never reach past
   * the section that owns this price. */
  function scopeOf(node) {
    return (
      (node.closest && (node.closest('.shopify-section') || node.closest('[data-section-id]'))) ||
      document
    );
  }

  function stripFor(node) {
    return scopeOf(node).querySelector(STRIP);
  }

  function isClaimed(strip) {
    if (!strip) return false;
    if (strip.getAttribute('data-state') === 'claimed') return true;
    var code = strip.getAttribute('data-coupon-code');
    if (!code) return false;
    try {
      return window.localStorage.getItem('afroyla:coupon:' + code) === '1';
    } catch (e) {
      return false;
    }
  }

  /* Mirrors the Liquid, the strip and the pill: floor the saving, then subtract.
   * Computing the payable directly is a cent under what Shopify charges. */
  function savingsFor(cents, strip) {
    var fixed = parseInt(strip.getAttribute('data-fixed-cents') || '0', 10);
    var pct = parseFloat(strip.getAttribute('data-discount-pct') || '0');
    var saving = 0;
    if (fixed > 0) {
      saving = fixed;
    } else if (pct > 0) {
      saving = Math.floor((cents * pct) / 100);
    }
    return Math.min(saving, cents);
  }

  /* --- the headline ----------------------------------------------------- */

  function wasNode(wrap, priceEl) {
    var was = wrap.querySelector('[data-coupon-was]');
    if (!was) {
      was = document.createElement('s');
      was.className = 'product__price--strike afroyla-coupon-was';
      was.setAttribute('data-coupon-was', '');
      priceEl.parentNode.insertBefore(was, priceEl.nextSibling);
    }
    return was;
  }

  /* The note names the code, because the headline sits far above the strip and
   * a price that dropped without saying why reads as a mistake. Skipped in the
   * sticky bar, which has one line of room and no space for a sentence.
   *
   * It lives INSIDE the price wrapper, not beside it: the featured price style
   * makes that wrapper a flex row that turns into a column under 749px, and a
   * note parked outside it floats next to the price instead of following it. */
  function noteNode(wrap) {
    if (wrap.closest('.cart-bar')) return null;
    var note = wrap.querySelector('[data-coupon-note]');
    if (!note) {
      note = document.createElement('span');
      note.className = 'afroyla-coupon-note';
      note.setAttribute('data-coupon-note', '');
      wrap.appendChild(note);
    }
    return note;
  }

  function paintHeadline(wrap) {
    var priceEl = wrap.querySelector(PRICE);
    if (!priceEl) return;

    var strip = stripFor(wrap);
    if (!strip) return;

    var claimed = isClaimed(strip);
    var base = parseInt(strip.getAttribute('data-price-cents') || '0', 10);
    if (!claimed || base <= 0) {
      restoreHeadline(wrap, priceEl);
      return;
    }

    var payable = base - savingsFor(base, strip);
    var desired = formatMoney(payable);

    /* Any text we did not write is `_updatePriceBlock` telling us the real
     * variant price — keep it, it is what unclaiming must restore to. */
    var current = priceEl.textContent.trim();
    if (current !== desired && current !== priceEl.getAttribute('data-coupon-mine')) {
      priceEl.setAttribute('data-coupon-was-text', current);
    }

    if (current !== desired) {
      priceEl.textContent = desired;
      priceEl.setAttribute('data-coupon-mine', desired);
    }

    /* The red lives in CSS, keyed off data-coupon-mine above, because the
     * featured price style pins `color: #000 !important` on the Druk headline
     * and only a more specific selector can outrank it. Nothing to toggle here.
     *
     * The native compare-at strike would be a third number next to the payable
     * and the base, so it stands down while a code is claimed. */
    var nativeCompare = wrap.querySelector('[data-compare-price]');
    if (nativeCompare && !nativeCompare.classList.contains('hidden')) {
      nativeCompare.classList.add('hidden');
      nativeCompare.setAttribute('data-coupon-hid', '1');
    }
    var nativeCompareText = wrap.querySelector('[data-compare-text]');
    if (nativeCompareText && !nativeCompareText.classList.contains('hidden')) {
      nativeCompareText.classList.add('hidden');
      nativeCompareText.setAttribute('data-coupon-hid', '1');
    }

    var was = wasNode(wrap, priceEl);
    var wasMoney = formatMoney(base);
    if (was.textContent.trim() !== wasMoney) was.textContent = wasMoney;
    was.hidden = false;

    /* Attribution, not explanation. The strip below already spells out what the
     * code does; up here the headline only has to say which code moved it, so
     * it borrows the buy box's own micro-label grammar (uppercase, letterspaced,
     * grey — the same face as SIZE and MATERIAL) and stays on the price line. */
    var note = noteNode(wrap);
    if (note) {
      var code = strip.getAttribute('data-coupon-code') || '';
      var line = code ? code + ' applied' : 'Coupon applied';
      if (note.textContent.trim() !== line) note.textContent = line;
      note.hidden = false;
    }

    syncPaymentTerms(wrap, true);
  }

  function restoreHeadline(wrap, priceEl) {
    var back = priceEl.getAttribute('data-coupon-was-text');
    if (back && priceEl.textContent.trim() === priceEl.getAttribute('data-coupon-mine')) {
      priceEl.textContent = back;
    }
    priceEl.removeAttribute('data-coupon-mine');
    priceEl.removeAttribute('data-coupon-was-text');

    wrap.querySelectorAll('[data-coupon-hid]').forEach(function (el) {
      el.classList.remove('hidden');
      el.removeAttribute('data-coupon-hid');
    });

    var was = wrap.querySelector('[data-coupon-was]');
    if (was) was.hidden = true;

    var note = wrap.querySelector('[data-coupon-note]');
    if (note) note.hidden = true;

    syncPaymentTerms(wrap, false);
  }

  /* Shop Pay's own component computes its instalments from the real variant
   * price and cannot be told about a discount code. Left visible it would sit
   * directly under the payable saying four payments that add back up to the
   * pre-coupon total. Owner decision 2026-08-14: hide it while a code is on. */
  function syncPaymentTerms(wrap, claimed) {
    var host = scopeOf(wrap);
    host.querySelectorAll('shopify-payment-terms').forEach(function (terms) {
      var box = terms.closest('.product__policies, .shopify-payment-terms') || terms;
      if (claimed) {
        if (!box.hasAttribute('data-coupon-terms-hid')) {
          box.setAttribute('data-coupon-terms-hid', box.style.display || '');
          box.style.display = 'none';
        }
      } else if (box.hasAttribute('data-coupon-terms-hid')) {
        box.style.display = box.getAttribute('data-coupon-terms-hid');
        box.removeAttribute('data-coupon-terms-hid');
      }
    });
  }

  /* --- the material cards ------------------------------------------------ */

  /* Cards are other variants of THIS product, so the claimed code covers each
   * one and the saving is the same floor applied to that variant's own price.
   * Card grammar stays black payable + muted strike, matching the set and
   * upsell rows it sits beside rather than the headline above it. */
  function paintCard(el) {
    var strip = stripFor(el);
    if (!strip) return;

    var cents = parseInt(el.getAttribute('data-card-cents') || '0', 10);
    if (cents <= 0) return;

    var claimed = isClaimed(strip);
    var show = claimed ? cents - savingsFor(cents, strip) : cents;
    var desired = formatMoney(show) + (claimed ? formatMoney(cents) : '');

    if (el.textContent.replace(/\s+/g, '') === desired.replace(/\s+/g, '')) return;

    el.textContent = '';
    el.appendChild(document.createTextNode(formatMoney(show)));
    if (claimed) {
      var s = document.createElement('s');
      s.className = 'hc-vpv2__card-was';
      s.textContent = formatMoney(cents);
      el.appendChild(s);
    }
  }

  /* --- lifecycle --------------------------------------------------------- */

  function paintAll(root) {
    if (painting) return;
    painting = true;
    try {
      (root || document).querySelectorAll(WRAP).forEach(function (wrap) {
        if (wrap.querySelector(PRICE)) paintHeadline(wrap);
      });
      (root || document).querySelectorAll(CARD_PRICE).forEach(paintCard);
    } finally {
      painting = false;
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(function () {
      scheduled = false;
      paintAll(document);
    });
  }

  [
    'afroyla:coupon:claimed',
    'afroyla:discount:applied',
    'afroyla:discount:removed',
    'theme:variant:change'
  ].forEach(function (name) {
    document.addEventListener(name, schedule);
  });

  paintAll(document);

  /* Catches `_updatePriceBlock`'s replays at +100/500/1500ms, the strip's own
   * state changes, and buy boxes injected by quick-add modals. */
  if (window.MutationObserver) {
    new MutationObserver(function () {
      if (painting) return;
      schedule();
    }).observe(document.documentElement, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['data-state', 'data-price-cents', 'data-savings-cents']
    });
  }
})();
